import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRegistration } from '../Registration';
import { prayerUnits } from '../../../data/prayerUnits';
import { useLanguage } from '../../../contexts/LanguageContext';

const Step4PrayerUnit = () => {
  const { formData, updateFormData, nextStep, prevStep } = useRegistration();
  const { t } = useLanguage();
  const copy = t('registration.steps.prayerUnit', {});
  const [selectedUnit, setSelectedUnit] = useState(formData.prayerUnit || '');
  
  const { handleSubmit } = useForm({
    defaultValues: { prayerUnit: selectedUnit }
  });

  const localizedUnits = useMemo(() => (
    prayerUnits.map((unit) => ({
      ...unit,
      name: t(`${unit.translationKey}.name`),
      description: t(`${unit.translationKey}.description`)
    }))
  ), [t]);

  const onSubmit = () => {
    if (selectedUnit) {
      const unit = prayerUnits.find((u) => u.id === selectedUnit);
      updateFormData({ 
        prayerUnit: selectedUnit,
        prayerUnitName: unit ? unit.internalName : ''
      });
      nextStep();
    }
  };

  const selectedUnitDetails = localizedUnits.find((unit) => unit.id === selectedUnit);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
          {copy.title}
        </h2>
        <p className="text-gray-600 text-center mb-8">
          {copy.description}
        </p>
      </div>

      {/* Prayer Units Grid */}
      <div className="grid grid-cols-1 gap-4 max-h-96 overflow-y-auto">
        {localizedUnits.map((unit) => (
          <div
            key={unit.id}
            className={`border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 ${
              selectedUnit === unit.id
                ? 'border-prayer-red bg-prayer-red/5 shadow-md'
                : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
            }`}
            onClick={() => setSelectedUnit(unit.id)}
          >
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <input
                  type="radio"
                  name="prayerUnit"
                  value={unit.id}
                  checked={selectedUnit === unit.id}
                  onChange={() => setSelectedUnit(unit.id)}
                  className="h-4 w-4 text-prayer-red focus:ring-prayer-red border-gray-300"
                />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">{unit.name}</h3>
                <p className="text-sm text-gray-600">{unit.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Selection Info */}
      {selectedUnitDetails && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">
                {copy.selectionMessage
                  ? copy.selectionMessage.replace('{unit}', selectedUnitDetails.name)
                  : selectedUnitDetails.name}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Prayer Unit Information */}
      <div className="bg-prayer-red/5 border border-prayer-red/20 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-prayer-red" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-prayer-red">
              {copy.info?.title}
            </h3>
            <p className="mt-1 text-sm text-prayer-red/80">
              {copy.info?.description}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6">
        <button
          type="button"
          onClick={prevStep}
          className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-all duration-200"
        >
          {t('registration.previous')}
        </button>
        
        <button
          type="submit"
          disabled={!selectedUnit}
          className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
            selectedUnit
              ? 'bg-prayer-red text-white hover:bg-prayer-red-light'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {t('registration.next')}
        </button>
      </div>
    </form>
  );
};

export default Step4PrayerUnit;
